import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Avatar,
  Box,
  CssBaseline,
  Paper,
  Typography
} from '@mui/material'
import GoogleIcon from '@mui/icons-material/Google'
import ROUTES from '../../routesConfig'
import TokenStorage from '../Api/TokenStorage'
import UserAuthContainer from '../Containers/UserAuthContainer'
import useNavigation from '../Utils/navigationUtils'
import { CONTENT_COLUMN_MAX_WIDTH, contentColumnPageShellSx } from '../UI/contentColumn'
import PageBackRow from '../UI/PageBackRow'

const GoogleOauthCallbackPage = () => {
  const userAuthContainer = UserAuthContainer.useContainer()
  const tokenStorage = useMemo(() => new TokenStorage(), [])
  const { navigateToPathWithReplace, navigateToLogin, resolveHomePath } = useNavigation()
  const hasHandledCallback = useRef(false)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (hasHandledCallback.current) return
    hasHandledCallback.current = true

    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''))
    const authToken = hashParams.get('auth-token')
    const acctType = hashParams.get('acct-type')
    const provider = hashParams.get('provider')
    const newUser = hashParams.get('new-user') === 'true'
    const oauthError = hashParams.get('error')
    const oauthErrorDescription = hashParams.get('error-description')

    const finishWithError = (message) => {
      tokenStorage.clearOauthReturnTarget()
      setError(new Error(message))
      setLoading(false)
    }

    const run = async () => {
      if (oauthError) {
        finishWithError(oauthErrorDescription || 'Google login could not be completed.')
        return
      }

      if (!authToken || !acctType) {
        finishWithError('Google login response was incomplete. Please try again.')
        return
      }

      const authMeta = {
        'auth-token': authToken,
        'acct-type': acctType,
      }
      if (provider) authMeta.provider = provider

      try {
        tokenStorage.setAuthMeta(authMeta)
        await userAuthContainer.loadUser(true)

        if (provider === 'google_oauth2' && newUser) {
          navigateToPathWithReplace({
            pathname: ROUTES.UPDATE_PROFILE,
            state: {
              oauthSignupCompletion: {
                provider,
                source: 'google_oauth'
              }
            }
          })
          return
        }

        const returnTarget = tokenStorage.getOauthReturnTarget() || ROUTES.HOME
        tokenStorage.clearOauthReturnTarget()
        navigateToPathWithReplace(returnTarget)
      } catch (err) {
        userAuthContainer.clearUserAndToken()
        finishWithError(err?.message || 'Unable to finish Google login. Please try again.')
      }
    }

    run()
  }, [navigateToPathWithReplace, resolveHomePath, tokenStorage, userAuthContainer])

  return (
    <Box sx={contentColumnPageShellSx}>
      <CssBaseline />
      <Paper
        sx={{
          mt: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 2,
          px: 3,
          pb: 3,
          bgcolor: '#0B0F19',
          color: '#E6E9EF',
          borderRadius: 3,
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)',
          width: '100%',
          boxSizing: 'border-box',
          maxWidth: CONTENT_COLUMN_MAX_WIDTH
        }}
      >
        {!loading && error && (
          <PageBackRow onClick={navigateToLogin} label="Back to login" sx={{ mb: 1 }} />
        )}
        <Avatar sx={{ m: 1, backgroundColor: 'rgba(6, 182, 212, 0.16)', color: '#22D3EE' }}>
          <GoogleIcon />
        </Avatar>
        <Typography component="h1" variant="h5" sx={{ color: '#E6E9EF', fontWeight: 800, letterSpacing: 0.2 }}>
          Google login
        </Typography>

        {loading && (
          <Typography
            variant="body1"
            sx={{ mt: 2, color: 'rgba(230,233,239,0.82)', textAlign: 'center', maxWidth: '30rem' }}
          >
            Finishing your Google sign-in now...
          </Typography>
        )}

        {!loading && error && (
          <>
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                width: '100%',
                bgcolor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)'
              }}
            >
              <Typography variant="body2" sx={{ color: '#EF4444', fontWeight: 500, lineHeight: 1.5, textAlign: 'center' }}>
                {error.message || 'Google login could not be completed.'}
              </Typography>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  )
}

export default GoogleOauthCallbackPage
